// @erp/module-lib/fe · scan/hub — the BROWSER-scoped scan link (SCAN-4).
//
// The production connector's UX (shippra scanStore.ts): pair a phone once, then
// navigate freely — scans keep flowing to whichever screen is open. This hub
// restores that lifetime WITHOUT the source's security model:
//
//   • the session stays `makeScanSession()` — random per pairing act, never the
//     source's SHA-256(userId) (deterministic, replayable, shared across every
//     desk of an account). The id persists in localStorage (link.ts), so the
//     WHOLE BROWSER shares one pairing: a reload or a new tab rejoins it (the
//     relay broadcasts to every desk socket on the session). `stop()` /
//     unpair-all ends it; a private window degrades to tab-scoped.
//   • gates (a)/(b)/(c) are untouched — ops stay cookie-authenticated, the desk
//     socket still rides Sec-WebSocket-Protocol, and `onScan` consumers STAGE
//     (prefill, add a row, run a read-only lookup); they never commit. The hub
//     exposes no API that could.
//
// The hub is lazy WHEN NOTHING IS PAIRED: with no stored session, no socket
// opens until a pairing surface calls `start()`. With a stored session,
// `useScanHub` resumes the link automatically, so after a reload any screen
// that registers a scan handler is listening again without a trip to the
// devices screen. Once live it survives SPA navigation until `stop()`.
import { createSignal, onCleanup } from "solid-js";
import type { Accessor } from "solid-js";
import { SCAN_SESSION_STORE_KEY, clearStoredScanSession, loadStoredScanSession, openScanLink } from "./link.js";
import type { ScanLink } from "./link.js";
import type { ScanEvent } from "./bridge.js";

export type ScanHandler = (ev: ScanEvent) => void;

export interface ScanHub extends ScanLink {
  /** Has this tab an open relay link? (false until `start()` or a resume.) */
  started: Accessor<boolean>;
  /** Open the relay link (idempotent) — reuses the browser's stored session, else mints. */
  start: () => void;
  /** Close the link and forget the persisted session — the next `start()` mints a fresh one. */
  stop: () => void;
  /**
   * Register a screen's staging handler; returns the unregister. Handlers are
   * additive (every registered one sees every scan) — a screen registers on
   * mount and unregisters on cleanup (use `useScanHub`).
   */
  onScan: (h: ScanHandler) => () => void;
}

// ── module singletons — one hub per tab, created on first import ──
const handlers = new Set<ScanHandler>();
const [current, setCurrent] = createSignal<{ link: ScanLink; dispose: () => void } | null>(null);

const dispatch = (ev: ScanEvent): void => {
  for (const h of handlers) h(ev);
};

const start = (): void => {
  if (current()) return;
  setCurrent(openScanLink({ onStagedForConfirmation: dispatch }));
};

/** Drop this tab's link WITHOUT touching the persisted session (reload-safe). */
const teardown = (): void => {
  const c = current();
  if (!c) return;
  c.dispose();
  setCurrent(null);
};

const stop = (): void => {
  teardown();
  // stop() is the operator ending the pairing for the whole browser, not just
  // this tab — other tabs follow via the storage listener below.
  clearStoredScanSession();
};

// ── cross-tab coherence — when ANOTHER tab replaces or clears the persisted
// session (stop, unpair-all, a fresh pairing), this tab follows: drop the dead
// link, and reopen on the replacement when one was written. Lazy tabs (no open
// link) stay lazy — `useScanHub` resumes them on their next screen mount.
// "storage" fires only for changes made by OTHER tabs, so this never loops.
if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  window.addEventListener("storage", (ev: StorageEvent) => {
    if (ev.key !== SCAN_SESSION_STORE_KEY) return;
    const c = current();
    if (!c || ev.newValue === c.link.session()) return;
    teardown();
    if (ev.newValue) start(); // openScanLink re-reads storage → joins the new session
  });
}

/** Read a link accessor through the reactive `current`, with an idle fallback. */
const via = <T>(pick: (l: ScanLink) => Accessor<T>, idle: T): Accessor<T> => {
  return () => {
    const c = current();
    return c ? pick(c.link)() : idle;
  };
};

const hub: ScanHub = {
  started: () => current() !== null,
  start,
  stop,
  onScan: (h) => {
    handlers.add(h);
    return () => handlers.delete(h);
  },
  session: via((l) => l.session, ""),
  pairingUrl: via((l) => l.pairingUrl, ""),
  code: via((l) => l.code, ""),
  devices: via((l) => l.devices, []),
  connected: via((l) => l.connected, false),
  staged: via((l) => l.staged, null),
  clearStaged: () => current()?.link.clearStaged(),
  recent: via((l) => l.recent, []),
  clearRecent: () => current()?.link.clearRecent(),
  regenerateCode: async () => current()?.link.regenerateCode(),
  removeDevice: async (id) => current()?.link.removeDevice(id),
  removeAllDevices: async () => current()?.link.removeAllDevices(),
};

/** The tab's shared scan hub. */
export function scanHub(): ScanHub {
  return hub;
}

/**
 * Screen-side hook: register this screen's staging handler for its mount
 * lifetime. Must be called inside a Solid owner. When the browser holds a
 * stored pairing session, this RESUMES the link automatically (a reload or a
 * new tab keeps listening); with nothing stored the hub stays lazy — a pairing
 * surface (PhoneScanModal, ConnectDevices) calls `hub.start()` when the
 * operator actually wants to pair.
 */
export function useScanHub(onScan?: ScanHandler): ScanHub {
  if (!current() && loadStoredScanSession() !== null) start();
  if (onScan) {
    const off = hub.onScan(onScan);
    onCleanup(off);
  }
  return hub;
}
